/**
 * JWT utility functions for token management
 */

/**
 * Decode a JWT token without verification (client-side only)
 * Returns the payload as an object
 */
export function decodeJWT(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token - The JWT token to check
 * @param bufferSeconds - Number of seconds before expiration to consider it expired (default: 300 = 5 minutes)
 * @returns true if token is expired or will expire within bufferSeconds
 */
export function isTokenExpired(token: string | null, bufferSeconds: number = 300): boolean {
  if (!token) {
    return true;
  }
  
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true; // If we can't decode or no expiration, consider it expired
  }
  
  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = bufferSeconds * 1000;
  
  // Token is expired if current time + buffer is past expiration
  return currentTime + bufferTime >= expirationTime;
}

/**
 * Get the expiration time of a JWT token in milliseconds
 * @returns expiration timestamp in milliseconds, or null if invalid
 */
export function getTokenExpiration(token: string | null): number | null {
  if (!token) {
    return null;
  }
  
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  return decoded.exp * 1000; // Convert to milliseconds
}
